// ticket-photos-for-valet — the client's request in their own words: "el
// valet TOMA las fotos al ingresar el vehiculo, pero despues no puede
// verlas nunca... deberia aparecer en esta aplicacion para que el valet
// parking las observe o las valide cuando el vehiculo sale". This panel is
// meant to be embedded in the delivery-confirm dialog (HomePage) so the
// valet can check the vehicle's photographed condition at the moment that
// matters — delivery.
//
// The 4 hard constraints from the brief, each addressed here:
//   1. Up to 24 photos (4 angles + up to 20 incident) → small thumbnails in
//      a grid, `loading="lazy"`, expand-on-click via PhotoLightbox — never
//      24 full-size images at once.
//   2. Flaky mobile connection → a broken thumbnail (`onError`) degrades to
//      a placeholder icon for THAT photo only; it never throws, never hides
//      the rest of the grid, and never touches the delivery button.
//   3. Signed URLs expire (5 min TTL, backend-enforced) → a broken
//      thumbnail surfaces a manual "reload" action that re-fetches fresh
//      URLs (`useTicketPhotos`'s `staleTime: 0` already forces a fresh
//      fetch on every mount/reopen; this covers the "left the dialog open
//      past 5 minutes" case where the mount itself is stale).
//   4. Never blocks delivery → renders `null` while `ticketId` is null
//      (dialog closed) and a network failure renders an inline
//      `role="alert"` + retry, never a thrown error or a spinner gating
//      the dialog's Confirm/Cancel footer (this component owns no part of
//      that footer).
import { useTranslation } from '@jq4race/i18n';
import type { PhotoAngle } from '@jq4race/shared';
import { Skeleton } from '@jq4race/ui';
import { ImageOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useTicketPhotos } from '../../hooks/use-ticket-photos';
import type { LightboxPhoto } from './PhotoLightbox';
import { PhotoLightbox } from './PhotoLightbox';

export interface TicketPhotosPanelProps {
  /** The ticket being delivered, or null while the delivery dialog is closed. */
  ticketId: string | null;
  /** Used to build meaningful alt text (e.g. "front photo of ABC-123"). */
  plate: string;
}

const ANGLE_LABEL_KEYS: Record<PhotoAngle, string> = {
  front: 'checkin:photoAngles.front',
  back: 'checkin:photoAngles.back',
  driver: 'checkin:photoAngles.driver',
  passenger: 'checkin:photoAngles.passenger',
};

export function TicketPhotosPanel({ ticketId, plate }: TicketPhotosPanelProps) {
  const { t } = useTranslation('pickup-queue');
  const { data, isLoading, isError, isFetching, refetch } = useTicketPhotos(ticketId);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // A fresh ticket (new dialog open) must start with a clean slate — stale
  // "broken" flags or an open lightbox from a PREVIOUS ticket must never
  // leak into the next one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ticketId is an intentional reset trigger, not a value read in the effect body
  useEffect(() => {
    setBrokenIds(new Set());
    setLightboxIndex(null);
  }, [ticketId]);

  const items: LightboxPhoto[] = useMemo(() => {
    if (!data) return [];
    const angled = data.photos.map((p) => ({
      id: p.id,
      url: p.url,
      alt: t('photosPanel.angleAlt', { angle: t(ANGLE_LABEL_KEYS[p.angle]), plate }),
    }));
    const incidents = data.incidentPhotos.map((p, i) => ({
      id: p.id,
      url: p.url,
      alt: t('photosPanel.incidentAlt', { index: i + 1, plate }),
    }));
    return [...angled, ...incidents];
  }, [data, plate, t]);

  const visibleItems = items.filter((item) => !brokenIds.has(item.id));

  if (ticketId === null) return null;

  if (isLoading) {
    return (
      <div className="grid grid-cols-4 gap-2" aria-hidden>
        {Array.from({ length: 4 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: decorative skeleton cells are static
          <Skeleton key={i} className="h-14 w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-2 rounded-sm border border-border bg-secondary px-3 py-2"
      >
        <span className="jq-caption text-muted-foreground">{t('photosPanel.loadError')}</span>
        <button
          type="button"
          onClick={() => void refetch()}
          className="jq-caption font-medium text-jq-gold-bright underline underline-offset-2"
        >
          {t('photosPanel.retry')}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="jq-caption text-muted-foreground">{t('photosPanel.empty')}</p>;
  }

  return (
    <section aria-label={t('photosPanel.heading')} className="flex flex-col gap-2">
      <p className="jq-caption font-semibold text-foreground">{t('photosPanel.heading')}</p>

      <div className="grid grid-cols-4 gap-2">
        {items.map((item) => {
          const broken = brokenIds.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              disabled={broken}
              onClick={() =>
                setLightboxIndex(visibleItems.findIndex((visible) => visible.id === item.id))
              }
              aria-label={
                broken ? t('photosPanel.brokenAlt') : t('photosPanel.openAria', { label: item.alt })
              }
              className="relative h-14 w-full overflow-hidden rounded-sm border border-border bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-default"
            >
              {broken ? (
                <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <ImageOff className="size-4" aria-hidden />
                </span>
              ) : (
                <img
                  src={item.url}
                  alt={item.alt}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={() =>
                    setBrokenIds((prev) => {
                      const next = new Set(prev);
                      next.add(item.id);
                      return next;
                    })
                  }
                />
              )}
            </button>
          );
        })}
      </div>

      {brokenIds.size > 0 ? (
        <div role="status" className="flex items-center justify-between gap-2">
          <span className="jq-caption text-muted-foreground">{t('photosPanel.expiredNotice')}</span>
          <button
            type="button"
            onClick={() => {
              setBrokenIds(new Set());
              void refetch();
            }}
            disabled={isFetching}
            className="jq-caption font-medium text-jq-gold-bright underline underline-offset-2 disabled:opacity-60"
          >
            {t('photosPanel.reload')}
          </button>
        </div>
      ) : null}

      {lightboxIndex !== null ? (
        <PhotoLightbox
          photos={visibleItems}
          index={Math.min(lightboxIndex, visibleItems.length - 1)}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}
    </section>
  );
}
